import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const POLL_INTERVAL_MS = 4000;

export function PennylaneEntityBackfillPanel() {
  const [previewPolling, setPreviewPolling] = useState(false);
  const [applyPolling, setApplyPolling] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (elapsedRef.current) {
      clearInterval(elapsedRef.current);
      elapsedRef.current = null;
    }
    setPreviewPolling(false);
    setApplyPolling(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // On mount: check for a previous apply job so returning users see
  // the persisted result (reload recovery).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.pennylaneEntityBackfillJobLatest('apply');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result && !job.result.error) {
          setApplyResult(job.result);
        } else if (job.status === 'running') {
          startTimeRef.current = Date.now();
          startPolling(job._id, 'apply');
        } else if (job.status === 'failed') {
          setApplyResult({ _failed: true, error: job.error });
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = useCallback((id, operation) => {
    stopPolling();
    if (operation === 'preview') setPreviewPolling(true);
    else setApplyPolling(true);

    startTimeRef.current = startTimeRef.current ?? Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await diagnosticsAPI.pennylaneEntityBackfillJobStatus(id);
        if (data.status === 'completed') {
          stopPolling();
          startTimeRef.current = null;
          if (operation === 'preview') {
            setPreview(data.result);
            if (data.result?.fetch_failed) {
              toast.error('Credential fetch failed. Preview cannot propose corrections against an incomplete entity index.');
            } else {
              toast.success('Preview complete');
            }
          } else {
            setApplyResult(data.result);
            if (data.result?.fetch_failed) {
              toast.error('Credential fetch failed. Apply aborted with zero writes.');
            } else {
              toast.success(`Backfill complete: ${data.result?.written ?? 0} corrections written`);
            }
            // Clear preview so Apply cannot be re-clicked without a fresh preview.
            setPreview(null);
          }
        } else if (data.status === 'failed') {
          stopPolling();
          startTimeRef.current = null;
          toast.error(data.error ?? `${operation} failed`);
        }
      } catch (err) {
        stopPolling();
        startTimeRef.current = null;
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  const runPreview = async () => {
    setDispatching(true);
    setPreview(null);
    setApplyResult(null);
    setElapsed(0);
    startTimeRef.current = Date.now();
    try {
      const { data } = await diagnosticsAPI.pennylaneEntityBackfillPreviewStart();
      startPolling(data.job_id, 'preview');
    } catch (err) {
      startTimeRef.current = null;
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const runApply = async () => {
    if (!preview || preview.proposed_count === 0) return;
    if (!window.confirm(
      `This will correct entity_id on ${preview.proposed_count} ` +
      `Pennylane invoice(s). Continue?`
    )) return;

    setDispatching(true);
    setApplyResult(null);
    setElapsed(0);
    startTimeRef.current = Date.now();
    try {
      const { data } = await diagnosticsAPI.pennylaneEntityBackfillApplyStart();
      startPolling(data.job_id, 'apply');
    } catch (err) {
      startTimeRef.current = null;
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const busy = dispatching || previewPolling || applyPolling;
  const canApply = preview && preview.proposed_count > 0 && !preview.fetch_failed && !busy;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Pennylane Invoice entity_id Backfill</CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">
          Corrects db.invoices entity_id on Pennylane-sourced invoices synced before the
          fix (previously hardcoded "bi_sas" regardless of which credential fetched them).
          Re-derives ownership by matching each invoice against a live per-credential fetch.
          Preview runs fresh; Apply re-fetches and writes unambiguous matches only.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Button onClick={runPreview} disabled={busy} size="sm">
            {previewPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          <Button
            onClick={runApply}
            disabled={!canApply}
            size="sm"
            variant={canApply ? 'default' : 'outline'}
          >
            {applyPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            {preview ? `Apply ${fmtInt(preview.proposed_count)} correction(s)` : 'Apply'}
          </Button>
          {(previewPolling || applyPolling) && (
            <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
              <RefreshCw className="h-3 w-3 animate-spin" />
              {elapsed}s
            </span>
          )}
        </div>

        {/* ── Preview: fetch failed ── */}
        {preview && preview.fetch_failed && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
              PREVIEW FAILED
            </Badge>
            <div className="border-2 rounded-lg p-3 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20 space-y-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                <p className="text-sm font-medium text-red-700 dark:text-red-400">
                  {preview.no_active_credentials
                    ? 'No active Pennylane credentials configured.'
                    : 'Credential fetch incomplete. Cannot propose corrections.'}
                </p>
              </div>
              {!preview.no_active_credentials && (
                <p className="text-xs text-red-600/80 dark:text-red-400/80">
                  Classification against a partial entity index would produce confident-wrong
                  results -- an invoice that fails to resolve for one credential might really
                  belong to that credential's entity.
                </p>
              )}
              {preview.unresolved_credentials && preview.unresolved_credentials.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-red-600">Unresolved credentials:</p>
                  {preview.unresolved_credentials.map((u, i) => (
                    <p key={i} className="text-[10px] text-red-600/80 font-mono pl-2">
                      {u.entity_id || u.credential_id}: {u.reason}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Generated: {preview.generated_at}
            </p>
          </div>
        )}

        {/* ── Preview result ── */}
        {preview && !preview.fetch_failed && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              PREVIEW (read-only)
            </Badge>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Total invoices</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(preview.total_pennylane_invoices)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Proposed corrections</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(preview.proposed_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-amber-200 dark:border-amber-800">
                <p className="text-xs text-muted-foreground">Ambiguous</p>
                <p className="text-lg font-semibold tabular-nums text-amber-600">{fmtInt(preview.ambiguous_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Unresolvable</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(preview.unresolvable_count)}</p>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Already correct: {fmtInt(preview.already_correct_count)} &middot; Entities fetched: {(preview.entities_fetched || []).join(', ') || '-'}
            </p>

            {/* Proposed corrections table */}
            {preview.proposed_corrections && preview.proposed_corrections.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                  Proposed corrections ({preview.proposed_corrections.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">invoice_number</th>
                        <th className="text-left px-2 py-1.5 font-medium">current entity_id</th>
                        <th className="text-left px-2 py-1.5 font-medium">correct entity_id</th>
                        <th className="text-left px-2 py-1.5 font-medium">type</th>
                        <th className="text-right px-2 py-1.5 font-medium">amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.proposed_corrections.map((w, i) => (
                        <tr key={w.invoice_id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{w.invoice_number}</td>
                          <td className="px-2 py-1 font-mono text-red-600">{w.current_entity_id}</td>
                          <td className="px-2 py-1 font-mono text-green-600">{w.correct_entity_id}</td>
                          <td className="px-2 py-1">{w.type}</td>
                          <td className="px-2 py-1 text-right font-mono">{w.amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {/* Ambiguous list */}
            {preview.ambiguous_list && preview.ambiguous_list.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-amber-600">
                  Ambiguous ({preview.ambiguous_list.length}) -- never auto-corrected
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {preview.ambiguous_list.map((a, i) => (
                        <tr key={a.invoice_id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{a.invoice_id}</td>
                          <td className="px-2 py-1">candidates: {(a.candidate_entity_ids || []).join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">
              Generated: {preview.generated_at}
            </p>
          </div>
        )}

        {/* ── Apply: fetch failed ── */}
        {applyResult && applyResult.fetch_failed && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
              APPLY ABORTED
            </Badge>
            <div className="border-2 rounded-lg p-3 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20 space-y-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                <p className="text-sm font-medium text-red-700 dark:text-red-400">
                  Credential fetch incomplete. Zero writes performed.
                </p>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Run: {applyResult.run_id} | Generated: {applyResult.generated_at}
            </p>
          </div>
        )}

        {/* ── Apply result ── */}
        {applyResult && !applyResult._failed && !applyResult.fetch_failed && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
              APPLIED
            </Badge>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Written</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(applyResult.written)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Conflicts skipped</p>
                <p className="text-lg font-semibold tabular-nums text-amber-600">{fmtInt(applyResult.skipped_conflict)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Ambiguous</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(applyResult.ambiguous_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Unresolvable</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(applyResult.unresolvable_count)}</p>
              </div>
            </div>

            {applyResult.written_samples && applyResult.written_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                  Written samples ({applyResult.written_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">invoice_number</th>
                        <th className="text-left px-2 py-1.5 font-medium">previous</th>
                        <th className="text-left px-2 py-1.5 font-medium">corrected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applyResult.written_samples.map((w, i) => (
                        <tr key={w.invoice_id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{w.invoice_number}</td>
                          <td className="px-2 py-1 font-mono text-red-600">{w.previous_entity_id}</td>
                          <td className="px-2 py-1 font-mono text-green-600">{w.corrected_entity_id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">
              Run: {applyResult.run_id} | Generated: {applyResult.generated_at}
            </p>
          </div>
        )}

        {/* ── Failed apply from previous session ── */}
        {applyResult && applyResult._failed && (
          <div className="space-y-2">
            <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
              PREVIOUS APPLY FAILED
            </Badge>
            <p className="text-xs text-red-600">{applyResult.error}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
